import React, { useRef, useState, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import './tooltip.scss';
import TooltipBody from './tooltip-body';

function Tooltip({
  className,
  delay = 100,
  zIndex,
  wrapperClass,
  direction: propDirection,
  content,
  children,
}) {
  const timeoutRef = useRef(null);

  const [active, setActive] = useState(false);
  const [leftPosition, setLeftPosition] = useState(0);
  const [topPosition, setTopPosition] = useState(0);
  const [direction, setDirection] = useState('bottom');

  const tooltipContainer = useRef(null);
  const tooltipHandler = useRef(null);

  const showTip = () => {
    timeoutRef.current = setTimeout(() => {
      setActive(true);
    }, delay);
  };

  const hideTip = () => {
    clearTimeout(timeoutRef.current);
    setActive(false);
  };

  useLayoutEffect(() => {
    if (!active) return;

    const handler = tooltipHandler.current;
    const tooltip = tooltipContainer.current;

    if (!handler || !tooltip) return;

    const rect = handler.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();

    const tooltipWidth = tooltipRect.width;
    const tooltipHeight = tooltipRect.height;

    const dir = propDirection || 'bottom';

    let left = 0;
    let top = 0;
    let finalDirection = dir;

    switch (dir) {
      case 'right':
        left = rect.right;
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        break;

      case 'top':
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        top = rect.top - tooltipHeight - 10;
        break;

      case 'left':
        left = rect.left - tooltipWidth;
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        break;

      case 'bottom-left':
        left = rect.left;
        top = rect.bottom;
        break;

      case 'center':
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        top = rect.bottom;
        break;

      default:
        left = rect.left + rect.width / 2 - tooltipWidth / 2;

        if (rect.bottom + tooltipHeight + 10 > window.innerHeight) {
          top = rect.top - tooltipHeight - 10;
          finalDirection = 'top';
        } else {
          top = rect.bottom + 8;
          finalDirection = 'bottom';
        }
    }

    setLeftPosition(left);
    setTopPosition(top);
    setDirection(finalDirection);
  }, [active, propDirection]);

  return (
    <span
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
      onMouseDown={hideTip}
      role="button"
      tabIndex={0}
      ref={tooltipHandler}
      className={`tooltip-wrapper${wrapperClass ? ` ${wrapperClass}` : ''}`}
    >
      {children}

      <TooltipBody
        ref={tooltipContainer}
        content={content}
        topPosition={topPosition}
        leftPosition={leftPosition}
        direction={propDirection || direction}
        className={className}
        active={active}
        zIndex={zIndex}
      />
    </span>
  );
}

export default Tooltip;

Tooltip.propTypes = {
  delay: PropTypes.number,
  direction: PropTypes.string,
  content: PropTypes.string,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  zIndex: PropTypes.number,
  wrapperClass: PropTypes.string,
};

Tooltip.defaultProps = {
  delay: 100,
  direction: '',
  content: '',
  className: '',
  zIndex: 1000000,
  wrapperClass: '',
};
